import { Lock } from "lucide-react";

import { Badge } from "@/core/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { Progress } from "@/core/components/ui/progress";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { SavingsVaultDto } from "@/features/savings/api/dto";
import { AmountMinor } from "@/features/shared/components/amount-minor";

/**
 * Carte soldes d'un coffre (détail `GET /SavingsVaults/{id}`) : solde
 * comptable, part en attente, part retirable (0 tant que le coffre est
 * bloqué), progression vers l'objectif et échéance de blocage.
 * Montants via `AmountMinor` (formatage + `NoTranslate`, guide §4.3).
 */
export function VaultBalanceCard({ vault }: { vault: SavingsVaultDto }) {
  const { t } = useTypedTranslation();
  const goal = vault.goalAmountMinor ?? null;
  const progress =
    vault.goalProgressPercent != null
      ? Math.min(100, Math.max(0, vault.goalProgressPercent))
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {t("savings.vaults.balance.title")}
          {vault.isLocked ? (
            <Badge variant="warning" className="gap-1">
              <Lock className="h-3 w-3" />
              {t("savings.vaults.detail.lockedBadge")}
              {vault.lockedUntil ? (
                <NoTranslate as="span">
                  {formatDateWithFallback(vault.lockedUntil, "")}
                </NoTranslate>
              ) : null}
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-sm text-muted-foreground">
              {t("savings.vaults.balance.total")}
            </dt>
            <dd>
              <AmountMinor
                amountMinor={vault.balanceMinor}
                currency={vault.currency}
                className="text-xl font-semibold"
              />
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">
              {t("savings.vaults.balance.pending")}
            </dt>
            <dd>
              <AmountMinor
                amountMinor={vault.pendingBalanceMinor}
                currency={vault.currency}
                className="text-xl font-semibold"
              />
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">
              {t("savings.vaults.balance.withdrawable")}
            </dt>
            <dd>
              <AmountMinor
                amountMinor={vault.withdrawableBalanceMinor}
                currency={vault.currency}
                className="text-xl font-semibold"
              />
            </dd>
            {vault.isLocked ? (
              <dd className="text-xs text-muted-foreground">
                {t("savings.vaults.balance.lockedHint")}
              </dd>
            ) : null}
          </div>
        </dl>

        {goal != null && progress != null ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t("savings.vaults.detail.goalProgress")}
              </span>
              <span className="inline-flex items-center gap-2">
                <AmountMinor
                  amountMinor={goal}
                  currency={vault.currency}
                  className="text-muted-foreground"
                />
                <NoTranslate as="span" className="font-medium">
                  {`${vault.goalProgressPercent} %`}
                </NoTranslate>
              </span>
            </div>
            <Progress value={progress} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
